import React from 'react';
import {
  View,
  StyleSheet,
  Pressable,
  PermissionsAndroid,
  Platform,
  FlatList,
  Linking,
  AppState,
  NativeEventSubscription,
} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import ScreenContext from '../../ScreenContext';
import withIsConnected from '../../hoc/withIsConnected';
import BaseScreen from '../BaseScreen';
import AVText from '../../elements/AVText';
import Styles from '../../Styles';
import NavActions from 'src/actions/NavActions';
import RequirePermission from '../../elements/RequirePermission';
import Contacts from 'react-native-contacts';
import Events from 'src/logging/Events';
import Localized from 'src/constants/AppStrings';
import FirebaseAnalytic from '../../../nativeModules/FirebaseAnalytic';
import Settings from 'src/Settings';
import CustomSearchbar from 'src/components/elements/CustomSearchField';
import SearchIcon from 'src/components/img/svg/SearchIcon';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import ContactListErrorLogo from 'src/components/img/svg/ContactListErrorLogo';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import {getDescriber} from 'src/components/screens/orderAhead/descriptor/DescriptorType';
import {scanAndPayTemplate} from 'src/components/screens/scanAndPay/templates/scanandPayThemeHelper';

type ContactItem = {
  firstName: string;
  lastName: string;
  email: string;
};

type ChooseContactScreenProps = {
  onContactSelect: (arg0: string) => void;
};

type ChooseContactScreenState = {
  loading: boolean;
  contacts: Array<ContactItem>;
  filteredContacts: Array<ContactItem>;
  searchText: string;
  contactsAccessCheck: boolean;
};

const {getPickupLocationStyles, contactPermissionErrorText} = getDescriber();

class ChooseContactScreen extends React.Component<
  ChooseContactScreenProps,
  ChooseContactScreenState
> {
  static contextType = ScreenContext;
  handleAppStateChangeSubscription: NativeEventSubscription;
  appState: string;
  constructor(props: ChooseContactScreenProps) {
    super(props);
    this.appState = 'active';
    this.renderContactItem = this.renderContactItem.bind(this);
    this.getKeyForListItem = this.getKeyForListItem.bind(this);
    this.onClickContact = this.onClickContact.bind(this);
    this.onSearchTextChanged = this.onSearchTextChanged.bind(this);
    this.chooseContactStateChange = this.chooseContactStateChange.bind(this);
    this.state = {
      loading: false,
      contacts: [],
      filteredContacts: [],
      searchText: '',
      contactsAccessCheck: false,
    };
  }

  componentDidMount() {
    FirebaseAnalytic.trackEvent('componentDidMount', 'ChooseContactScreen', {
      ...this.props,
      ...this.state,
    });
    this.checkContactsPermission();
    if (Platform.OS === 'ios') {
      this.loadContacts();
    }
    this.handleAppStateChangeSubscription = AppState.addEventListener(
      'change',
      this.chooseContactStateChange,
    );
  }

  chooseContactStateChange(currentAppState: string) {
    if (currentAppState !== this.appState) {
      this.appState = currentAppState;
      if (currentAppState === 'active') {
        this.checkContactsPermission();
      }
    }
  }
  handleChangeSettings = () => {
    if (Platform.OS === 'ios') {
      Linking.openURL('app-settings:');
    } else {
      Linking.openSettings();
    }
  };
  checkContactsPermission = async () => {
    const permission = await Contacts.checkPermission();
    if (permission === 'authorized') {
      this.setState({contactsAccessCheck: true});
      this.loadContacts();
    }
  };

  onPermissionChange = (granted: string) => {
    if (granted === PermissionsAndroid.RESULTS.GRANTED) {
      this.setState({contactsAccessCheck: true});
      this.loadContacts();
    }
  };

  async loadContacts() {
    const contactsArray: Array<ContactItem> = [];

    try {
      const contacts = await Contacts.getAll();
      contacts.map((contact) => {
        contact.emailAddresses?.forEach((emailAddress) => {
          contactsArray.push({
            firstName: contact.givenName,
            lastName: contact.familyName,
            email: emailAddress.email,
          });
        });
      });
      contactsArray.sort(function (a, b) {
        const nameA = a.firstName.toLowerCase();
        const nameB = b.firstName.toLowerCase();

        if (nameA < nameB) {
          return -1;
        }

        if (nameA > nameB) {
          return 1;
        }

        return 0;
      });
      this.setState(
        {
          contacts: contactsArray,
          loading: false,
        },
        () => {
          FirebaseAnalytic.trackEvent('loadContacts', 'ChooseContactScreen', {
            ...this.props,
            ...this.state,
          });
        },
      );
    } catch (err) {
      this.setState({
        loading: false,
      });
      CrashlyticsEvents.log(
        'Exception',
        'ChooseContactScreen:loadContacts',
        err.message ? err.message : err.toString(),
      );
      Events.Error.trackEvent(
        'Exception',
        'ChooseContactScreen:loadContacts',
        err.message ? err.message : err.toString(),
      );
    }

    Contacts.checkPermission();
  }

  getKeyForListItem(item) {
    return item.email;
  }

  onClickContact(email) {
    FirebaseAnalytic.trackEvent('onClickContact', 'ChooseContactScreen', {
      ...this.props,
      ...this.state,
      email,
    });
    this.props.onContactSelect(email);
    NavActions.pop();
  }

  onSearchTextChanged(value) {
    const contacts = this.state.contacts;
    const searchText = value.trim().toLowerCase();
    const filteredContacts = contacts.filter((contact) => {
      return (
        `${contact.firstName} ${contact.lastName}`
          .toLowerCase()
          .startsWith(searchText) ||
        contact.lastName.toLowerCase().startsWith(searchText)
      );
    });
    this.setState(
      {
        searchText: value,
        filteredContacts,
      },
      () => {
        FirebaseAnalytic.trackEvent(
          'onSearchTextChanged',
          'ChooseContactScreen',
          {
            ...this.props,
            ...this.state,
          },
        );
      },
    );
  }

  renderContactItem({item}) {
    return (
      <Pressable
        onPress={() => this.onClickContact(item.email)}
        nativeID={'Btn onClickContact'}
      >
        <View style={styles.contactItemContainer}>
          <AVText
            style={styles.nameTextStyle}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
          >
            {item.firstName} {item.lastName}
          </AVText>
          <AVText
            style={styles.emailTextStyle}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
          >
            {item.email}
          </AVText>
        </View>
      </Pressable>
    );
  }

  render() {
    return (
      <BaseScreen title={Localized.Labels.choose_contact}>
        <View style={styles.content}>
          {
            <>
              <CustomSearchbar
                onChangeText={this.onSearchTextChanged}
                value={this.state.searchText}
                placeholder={Localized.Labels.search_contacts}
                containerStyle={[styles.searchWrapper]}
                elevation={2}
                inputStyle={styles.searchInput}
                searchIcon={<SearchIcon />}
                testID="qa_fld_Search"
              />
              {this.state.contactsAccessCheck && (
                <FlatList
                  refreshing={false}
                  data={
                    this.state.searchText === ''
                      ? this.state.contacts
                      : this.state.filteredContacts
                  }
                  renderItem={this.renderContactItem}
                  keyExtractor={this.getKeyForListItem}
                />
              )}
              {!this.state.contactsAccessCheck && (
                <View style={[styles.errorView]}>
                  <View
                    style={
                      !Settings.isRefiveAnd365Pay() && styles.errorContentView
                    }
                  >
                    <View style={styles.errorLogo}>
                      <ContactListErrorLogo />
                    </View>
                    <AVText
                      accessible={true}
                      accessibilityHint={
                        Localized.Labels.choose_contact_permission_error
                      }
                      accessibilityLabel={
                        Localized.Labels.choose_contact_permission_error
                      }
                      aria-label={
                        Localized.Labels.choose_contact_permission_error
                      }
                      maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
                      style={styles.errorText}
                    >
                      {contactPermissionErrorText()}
                    </AVText>
                  </View>
                  <View
                    style={
                      Settings.isRefiveAnd365Pay()
                        ? styles.defaultBtnWrapper
                        : styles.bottomBtnWrapper
                    }
                  >
                    <RoundedAppButton
                      buttonType={RoundedAppButtonType.Outline}
                      buttonViewStyle={[
                        getPickupLocationStyles().buttonView,
                        styles.roundedBtColor,
                      ]}
                      buttonContainerStyle={[
                        getPickupLocationStyles().containerStyle,
                        ,
                      ]}
                      accessibilityLabelValue={Localized.Buttons.go_to_settings}
                      onPress={this.handleChangeSettings}
                      titleText={Localized.Buttons.go_to_settings}
                      accessibilityHintValue={'button'}
                      aria-label={Localized.Buttons.go_to_settings}
                      textStyle={[getPickupLocationStyles().buttonText]}
                      maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
                    />
                  </View>
                </View>
              )}
            </>
          }

          <RequirePermission
            permission={PermissionsAndroid.PERMISSIONS.READ_CONTACTS}
            onPermissionChange={this.onPermissionChange}
          />
        </View>
      </BaseScreen>
    );
  }
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
    paddingHorizontal: Settings.isNewUI() ? Styles.Spacing.m2 : 0,
    justifyContent: 'center',
  },
  contactItemContainer: {
    alignItems: 'flex-start',
    flexDirection: 'column',
    justifyContent: 'center',
    borderBottomColor: Styles.lightGray,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  nameTextStyle: {
    color: Styles.black,
    fontSize: Styles.Fonts.f2,
    marginHorizontal: Styles.Spacing.m3,
  },
  emailTextStyle: {
    color: Styles.darkColor,
    fontSize: Styles.Fonts.f1,
    marginHorizontal: Styles.Spacing.m3,
    marginBottom: Styles.Spacing.m1,
  },
  searchWrapper: {
    borderRadius: 32,
    backgroundColor: Styles.white,
    marginHorizontal: Styles.Spacing.m3,
    marginTop: Styles.Spacing.m3,
    marginBottom: Styles.Spacing.m3,
    height: 51,
    borderWidth: 1,
    borderColor: Styles.Colors.PayNew.neutralHuesBase09,
    alignItems: 'center',
    ...Styles.Style.shadow,
    borderBottomWidth: Platform.OS === 'android' ? 1 : 0,
  },

  searchInput: {
    fontSize: Styles.Fonts.f2,
    fontWeight: '400',
    fontFamily: scanAndPayTemplate.textFontFamily,
    color: Styles.Colors.PayNew.darkHuesBase04,
  },
  settingBtn: {
    alignSelf: 'center',
    marginTop: Styles.Spacing.m2,
    paddingHorizontal: 12,
    borderWidth: 2,
  },
  errorView: {
    flex: 1,
    justifyContent: 'flex-start',
    paddingHorizontal: Styles.Fonts.f0 + Styles.Fonts.f0,
    top: Styles.Fonts.f1 * 3,
  },
  errorLogo: {
    alignSelf: 'center',
    marginBottom: 14,
  },
  errorText: {
    fontSize: Styles.Fonts.f7,
    fontWeight: '400',
    fontFamily: scanAndPayTemplate.textFontFamily,
    textAlign: 'center',
    marginBottom: Styles.Fonts.f0,
  },
  errorButton: {
    color: Styles.footerTabActive,
    fontSize: Styles.Fonts.f1,
    fontWeight: '700',
    fontFamily: scanAndPayTemplate.textFontFamily,
  },
  defaultBtnWrapper: {
    alignSelf: 'center',
    marginTop: Styles.Spacing.m2,
  },

  bottomBtnWrapper: {
    position: 'absolute',
    bottom: Styles.Spacing.m5,
    alignItems: 'center',
    marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m2,
    marginTop: Styles.Spacing.m3,
    alignSelf: 'center',
    width: Platform.OS === 'web' ? '100%' : null,
  },
  roundedBtColor: {
    borderWidth: 2,
    backgroundColor: Styles.white,
  },
  errorContentView: {
    paddingVertical: Styles.Spacing.m4,
  },
});

export default withForwardedNavigationParams()(
  withIsConnected(ChooseContactScreen),
);
